import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { UserContext } from '../../context/UserContext'; // Adjust to your context path
import './SubscriptionSuccess.css';

// const API_URL = 'http://localhost:6001/api'; // Local development
const API_URL = process.env.REACT_APP_API_URL || 'https://app.adsmash.org/api'; // Live URL

const SubscriptionSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [pollingCount, setPollingCount] = useState(0);
  const [subscriptionActive, setSubscriptionActive] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { updateUser } = useContext(UserContext); // Adjust based on your state management
  
  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const isSuccess = queryParams.get('success') === 'true';
  const sessionId = queryParams.get('session_id');

  useEffect(() => {
    // If not coming from a successful payment, redirect
    if (!isSuccess) {
      setLoading(false);
      navigate('/dashboard/plans');
      return;
    }
    
    // Function to poll for subscription status
    const pollSubscriptionStatus = async () => {
      try {
        // Get the latest user data
        const response = await axios.get(`${API_URL}/user/me`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const userData = response.data.user;
        
        // Update user state
        if (updateUser) {
          updateUser(userData);
        }
        
        // Check if subscription is active
        if (userData.subscription && 
            (userData.subscription.status === 'active' || 
             userData.subscription.status === 'trialing')) {
          setSubscriptionActive(true);
          setLoading(false);
          
          // Store subscription details in localStorage for immediate access
          localStorage.setItem('subscription_status', userData.subscription.status);
          localStorage.setItem('subscription_plan', userData.subscription.plan);
          
          // Redirect to dashboard after a delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
          
          return;
        }
        
        // If we've polled 5 times (10 seconds) and still no subscription
        if (pollingCount >= 5) {
          // Try direct verification with session ID if available
          if (sessionId) {
            await verifySessionDirectly(sessionId);
          } else {
            setLoading(false);
          }
          return;
        }
        
        // Increment polling count and try again in 2 seconds
        setPollingCount(prev => prev + 1);
        setTimeout(pollSubscriptionStatus, 2000);
        
      } catch (error) {
        console.error('Error checking subscription status:', error);
        setLoading(false);
      }
    };
    
    // Function to directly verify session with Stripe
    const verifySessionDirectly = async (sessionId) => {
      try {
        const response = await axios.post(`${API_URL}/subscription/verify-session`, 
          { sessionId },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const data = response.data;
        
        if (data.success && data.subscription) {
          setSubscriptionActive(true);
          
          // Update user context
          if (updateUser && data.user) {
            updateUser(data.user);
          }
          
          // Store subscription details
          if (data.subscription) {
            localStorage.setItem('subscription_status', data.subscription.status);
            localStorage.setItem('subscription_plan', data.subscription.plan);
          }
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error verifying session directly:', error);
        setLoading(false);
      }
    };
    
    // Start polling when component mounts
    pollSubscriptionStatus();
    
  }, [isSuccess, navigate, pollingCount, sessionId, updateUser]);

  // Loading state
  if (loading) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
      >
        <div className="subscription-success-loading">
          <div className="loading-spinner"></div>
          <p style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Processing your subscription...</p>
          <p className="loading-subtext">This may take a few moments</p>
        </div>
      </div>
    );
  }

  // Success state
  if (subscriptionActive) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
      >
        <div className="subscription-success-container">
          <div className="success-icon">✓</div>
          <h2>Subscription Activated!</h2>
          <p>Your subscription has been successfully activated.</p>
          <p>You'll be redirected to your dashboard shortly.</p>
          <button onClick={() => navigate('/dashboard')} className="dashboard-button">
            Go to Dashboard Now
          </button>
        </div>
      </div>
    );
  }

  // Default state (if subscription not active yet)
  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'linear-gradient(to bottom, #0c0d1f, #1b1324)' }}
    >
      <div className="subscription-processing-container">
        <h2>Payment Received</h2>
        <p>We've received your payment and are finalizing your subscription.</p>
        <p>This process may take a few minutes to complete.</p>
        <p>You can safely proceed to your dashboard and refresh the page later to see your updated subscription.</p>
        <div className="action-buttons">
          <button onClick={() => navigate('/dashboard')} className="dashboard-button">
            Go to Dashboard
          </button>
          <button onClick={() => window.location.reload()} className="refresh-button">
            Refresh Status
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess; 